import { isUrl } from './ParamsDisplayComponents';
import { IMAGE_EXTENSIONS } from './galleryParams.consts';

export interface ImageInput {
  id: string;
  url: string;
  thumbnailUrl: string;
  order: number;
}

export const isImageUrl = (value: string): boolean => {
  if (!isUrl(value)) return false;
  try {
    const { pathname } = new URL(value);
    const dotIndex = pathname.lastIndexOf('.');
    if (dotIndex === -1) return false;
    return IMAGE_EXTENSIONS.includes(pathname.slice(dotIndex).toLowerCase() as (typeof IMAGE_EXTENSIONS)[number]);
  } catch {
    return false;
  }
};

/**
 * Detects string URL values that represent image inputs after WASM round-trip
 * (where MediaAsset objects are serialized to plain URL strings).
 * Multi-URL values come from validateInputInfo joining arrays with '\n'.
 *
 * A URL is treated as an image if its pathname ends with a known image extension.
 */
export const extractUrlImageInputs = (
  inputs: Record<string, unknown>,
): { urlImageInputs: ImageInput[]; imageUrlKeys: Set<string> } => {
  const imageUrlKeys = new Set<string>();
  const urlImageInputs: ImageInput[] = [];

  for (const [key, value] of Object.entries(inputs)) {
    if (typeof value !== 'string') continue;

    const urls = value.split('\n').filter(isImageUrl);
    if (urls.length === 0) continue;

    for (let i = 0; i < urls.length; i++) {
      urlImageInputs.push({
        id: `${key}_${i}`,
        url: urls[i],
        thumbnailUrl: urls[i],
        order: i,
      });
    }
    imageUrlKeys.add(key);
  }

  return { urlImageInputs, imageUrlKeys };
};

export const extractValue = (value: string | { value: string } | undefined): string | undefined => {
  return typeof value === 'object' && value !== null && 'value' in value ? value.value : value;
};

const extractSeed = (seed: string | { seed: string } | undefined): string | undefined => {
  return typeof seed === 'object' && seed !== null && 'seed' in seed ? seed.seed : seed;
};

export interface ProcessedInputs {
  sortedParams: Record<string, unknown>;
  prompt: string | { value: string } | undefined;
  negativePrompt: string | { value: string } | undefined;
  imageInputs: ImageInput[] | undefined;
}

export const processInputs = (inputs: Record<string, unknown>): ProcessedInputs => {
  const negative = Object.entries(inputs).find(([key]) => key.includes('prompt') && key.includes('negative'))?.[1];

  const [promptKey, promptValue] =
    Object.entries(inputs).find(([key]) => key === 'prompt') ||
    Object.entries(inputs).find(([key]) => key.includes('prompt') && !key.includes('negative')) ||
    [];

  const { urlImageInputs, imageUrlKeys } = extractUrlImageInputs(inputs);
  const imageInputs = urlImageInputs.length > 0 ? urlImageInputs : undefined;

  const remainingParams = Object.fromEntries(
    Object.entries(inputs)
      .filter(([key]) => key !== promptKey && !imageUrlKeys.has(key))
      .map(([key, value]) =>
        key === 'seed' ? [key, String(extractSeed(value as string | { seed: string } | undefined))] : [key, value],
      ),
  );
  const sortedParams = Object.fromEntries(
    Object.entries(remainingParams).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)),
  );

  return {
    sortedParams,
    prompt: promptValue as string | { value: string } | undefined,
    negativePrompt: negative as string | { value: string } | undefined,
    imageInputs,
  };
};
